import type { BondOrder } from '../domain/editor.ts';
import type { JSMol } from '@rdkit/rdkit';
import type { DrawingDocument, StereoLigand } from '../model';
import {
  createDrawingDocumentFromChemicalStructure,
  type ChemicalStructureAtom,
  type ChemicalStructureBond
} from '../domain/graphDrawing.ts';
import type { ChemMoleculeV1Document } from '../domain/moleculeProjection.ts';
import type { ChemIsomerV1Document } from '../domain/isomerDocument.ts';
import type { PaperSize } from '../domain/geometry.ts';
import { createRdKitIndexMapping, documentToRdKitJson, rdkitMoleculeForLayout } from './chemicalValidation.ts';
import { parseMolBlockGeometry } from './smilesImport.ts';
import { moleculeIsomerToSmiles } from './moleculeToSmiles.ts';
import { addHaptoBond, placeHaptoLigands } from '../domain/haptoBonds.ts';
import { swapWedgesFrom } from '../domain/atomGeometryMutations.ts';
import { loadRdKit } from './rdkitAdapter.ts';

function aromaticIdSets(molecule: ChemMoleculeV1Document): {
  readonly atomIds: ReadonlySet<string>;
  readonly bondIds: ReadonlySet<string>;
} {
  const aromaticity = molecule.derivedPropertySets.find((set) => set.kind === 'aromaticity');
  return {
    atomIds: new Set(aromaticity?.atomIds ?? []),
    bondIds: new Set(aromaticity?.bondIds ?? [])
  };
}

// Isomer stereochemistry references Molecule atom/edge ids (contracts/isomer.schema.json), but
// createDrawingDocumentFromChemicalStructure assigns each atom/bond a brand-new sequential id
// based on its position in the atoms/bonds arrays passed in. Remap the Isomer's ligand/reference
// ids onto those new ids by array position, rather than by recomputing winding from scratch --
// re-deriving a tetrahedral ligand order from graph connectivity (as the SMILES-import path does)
// would only reproduce the Isomer's intended chirality if that derived order happened to match
// the Isomer's own stored ligand order; carrying the ligand list through verbatim (just relabeled)
// is correct regardless of ordering.
function remapStereochemistry(
  isomer: ChemIsomerV1Document,
  moleculeNodeIdToIndex: ReadonlyMap<string, number>,
  moleculeEdgeIdToIndex: ReadonlyMap<string, number>,
  newNodeIdByIndex: readonly string[],
  newEdgeIdByIndex: readonly string[]
): DrawingDocument['structure']['stereochemistry'] {
  const remapLigand = (ligand: StereoLigand): StereoLigand => {
    if ('implicitHydrogen' in ligand) return ligand;
    const index = moleculeNodeIdToIndex.get(ligand.atomId);
    if (index === undefined) throw new Error(`Isomer references unknown Molecule atom ${ligand.atomId}.`);
    return { atomId: newNodeIdByIndex[index]! };
  };
  const tetrahedral = Object.fromEntries(
    Object.entries(isomer.stereochemistry.tetrahedral).flatMap(([atomId, stereo]) => {
      const index = moleculeNodeIdToIndex.get(atomId);
      if (index === undefined) return [];
      return [
        [
          newNodeIdByIndex[index]!,
          {
            winding: stereo.winding,
            ligands: stereo.ligands.map(remapLigand) as [StereoLigand, StereoLigand, StereoLigand, StereoLigand]
          }
        ]
      ];
    })
  );
  const doubleBonds = Object.fromEntries(
    Object.entries(isomer.stereochemistry.doubleBonds).flatMap(([edgeId, stereo]) => {
      const index = moleculeEdgeIdToIndex.get(edgeId);
      if (index === undefined) return [];
      return [
        [
          newEdgeIdByIndex[index]!,
          {
            relativeConfiguration: stereo.relativeConfiguration,
            referenceAtomIds: stereo.referenceAtomIds.map((atomId) => {
              const atomIndex = moleculeNodeIdToIndex.get(atomId);
              if (atomIndex === undefined) throw new Error(`Isomer references unknown Molecule atom ${atomId}.`);
              return newNodeIdByIndex[atomIndex]!;
            }) as [string, string]
          }
        ]
      ];
    })
  );
  return { tetrahedral, doubleBonds };
}

function moleculeEdgeIndexMaps(molecule: ChemMoleculeV1Document): ReadonlyMap<string, number> {
  return new Map(molecule.graph.edges.map((edge, index) => [edge.id, index]));
}

// A structure-only placeholder document: enough for documentToMolBlock to serialize connectivity,
// atoms, bonds, and formal charges. Coordinates are dummies -- RDKit's get_new_coords() recomputes
// a fresh 2D layout from connectivity alone, ignoring whatever placeholder positions are fed in.
export function placeholderDocumentForCoordinateGeneration(molecule: ChemMoleculeV1Document): DrawingDocument {
  return {
    schemaVersion: 'chem-drawing.v10',
    structure: {
      id: molecule.id,
      revision: molecule.revision,
      // ADR 0026: the molecule's hapto bonds come back as the editor's edges from the metal.
      graph: {
        id: molecule.id,
        revision: molecule.revision,
        nodes: molecule.graph.nodes,
        edges: [
          ...molecule.graph.edges,
          ...Object.entries(molecule.haptoBonds ?? {}).map(([id, bond]) => ({
            id,
            source: bond.metalAtomId,
            target: bond.ligandAtomIds[0]
          }))
        ]
      },
      atoms: molecule.atoms,
      bonds: {
        ...molecule.bonds,
        ...Object.fromEntries(
          Object.entries(molecule.haptoBonds ?? {}).map(([id, bond]) => [
            id,
            { order: 0 as const, type: 'hapto' as const, ligandAtomIds: bond.ligandAtomIds }
          ])
        )
      },
      formalCharges: molecule.formalCharges,
      stereochemistry: { tetrahedral: {}, doubleBonds: {} }
    },
    depiction: {
      id: 'placeholder-depiction',
      revision: 1,
      structureId: molecule.id,
      structureRevision: molecule.revision,
      coordinateSet: {
        id: 'placeholder-coords',
        kind: 'depiction-2d',
        structureId: molecule.id,
        structureRevision: molecule.revision,
        dimensions: 2,
        units: 'drawing-unit',
        width: molecule.graph.nodes.length,
        height: 1,
        // Distinct dummy x per node avoids degenerate (zero-length) bonds in the placeholder
        // molblock; the exact values are discarded once RDKit generates real coordinates.
        coordinates: Object.fromEntries(molecule.graph.nodes.map((node, index) => [node.id, { x: index, y: 0 }]))
      },
      atomLabels: {},
      bondStyles: {},
      bondLengthOverrides: {}
    },
    annotations: [],
    derivedPropertySets: []
  };
}

/**
 * #1178: the Molecule's double bonds that can be E or Z, by edge ID. RDKit marks those "either" (3)
 * when it lays out a structure it read without coordinates -- the placeholder's dummy coordinates
 * would make it mark every double bond, `CC(C)=CC` included, so it reads RDKit's JSON here. None if
 * RDKit can't read the structure: the bonds are then drawn plain, as before.
 */
async function stereogenicDoubleBonds(molecule: ChemMoleculeV1Document): Promise<ReadonlySet<string>> {
  const placeholder = placeholderDocumentForCoordinateGeneration(molecule);
  const { nodeIdByAtomIndex } = createRdKitIndexMapping(placeholder);
  const rdkit = await loadRdKit();
  let rdkitMolecule: JSMol | null = null;
  try {
    rdkitMolecule = rdkit.get_mol(documentToRdKitJson(placeholder));
    if (!rdkitMolecule?.is_valid()) return new Set();
    const { bondStereo } = parseMolBlockGeometry(rdkitMolecule.get_new_coords(), nodeIdByAtomIndex.length);
    const open = new Set<string>();
    for (const bond of bondStereo) {
      if (bond.code !== 3) continue;
      const ends = [nodeIdByAtomIndex[bond.fromAtomIndex], nodeIdByAtomIndex[bond.toAtomIndex]];
      const edge = molecule.graph.edges.find(
        (candidate) =>
          ends.includes(candidate.source) &&
          ends.includes(candidate.target) &&
          molecule.bonds[candidate.id]?.order === 2
      );
      if (edge) open.add(edge.id);
    }
    return open;
  } catch {
    return new Set();
  } finally {
    rdkitMolecule?.delete();
  }
}

/** #1178: the double bonds `isomer` leaves open -- those that can be E or Z, less those it defines. */
export async function openDoubleBonds(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document
): Promise<ReadonlySet<string>> {
  const stereogenic = await stereogenicDoubleBonds(molecule);
  return new Set([...stereogenic].filter((edgeId) => !(edgeId in isomer.stereochemistry.doubleBonds)));
}

// A fresh 2D layout for a bare Molecule (no Isomer, so no stereochemistry): correct
// connectivity / atoms / charges / isotopes / radicals / aromaticity / coordinates, with
// depiction.bondStyles empty. RDKit's get_new_coords() lays out from connectivity alone. This
// is the molecule-only core shared by generateFlatIsomerDepiction (which layers an Isomer's
// stereochemistry on top) and by callers that only have a Molecule id -- e.g. search-result
// thumbnails (#191).
export async function generateMoleculeDepiction(
  molecule: ChemMoleculeV1Document,
  paperSize: PaperSize,
  // #1178: a Molecule fixes no E/Z, so each double bond that can be E or Z is drawn crossed.
  open?: ReadonlySet<string>
): Promise<DrawingDocument> {
  const crossed = open ?? (await stereogenicDoubleBonds(molecule));
  const placeholder = placeholderDocumentForCoordinateGeneration(molecule);
  const rdkit = await loadRdKit();
  let rdkitMolecule: JSMol | null = null;
  let coordinates: readonly { readonly x: number; readonly y: number }[];
  try {
    rdkitMolecule = rdkitMoleculeForLayout(rdkit, placeholder);
    if (!rdkitMolecule?.is_valid()) {
      throw new Error('RDKit could not sanitize this Molecule structure for coordinate generation.');
    }
    coordinates = parseMolBlockGeometry(rdkitMolecule.get_new_coords(), molecule.graph.nodes.length).coordinates;
  } finally {
    rdkitMolecule?.delete();
  }

  const moleculeNodeIdToIndex = new Map(molecule.graph.nodes.map((node, index) => [node.id, index]));
  const { atomIds: aromaticAtomIds, bondIds: aromaticBondIds } = aromaticIdSets(molecule);
  const placed = withHaptoLigandsPlaced(
    molecule,
    new Map(molecule.graph.nodes.map((node, index) => [node.id, coordinates[index]!]))
  );

  const atoms: ChemicalStructureAtom[] = molecule.graph.nodes.map((node) => {
    const atom = molecule.atoms[node.id]!;
    return {
      atomicNumber: atom.atomicNumber,
      formalCharge: molecule.formalCharges[node.id]!,
      x: placed.get(node.id)!.x,
      y: placed.get(node.id)!.y,
      aromatic: aromaticAtomIds.has(node.id),
      ...(atom.isotope !== undefined ? { isotope: atom.isotope } : {}),
      ...(atom.radicalElectronCount !== undefined ? { radicalElectronCount: atom.radicalElectronCount } : {})
    };
  });
  const bonds: ChemicalStructureBond[] = molecule.graph.edges.map((edge) => ({
    sourceAtomIndex: moleculeNodeIdToIndex.get(edge.source)!,
    targetAtomIndex: moleculeNodeIdToIndex.get(edge.target)!,
    order: molecule.bonds[edge.id]!.order as BondOrder,
    aromatic: aromaticBondIds.has(edge.id),
    ...(crossed.has(edge.id) ? { crossed: true } : {})
  }));

  return withHaptoBonds(
    createDrawingDocumentFromChemicalStructure(paperSize, atoms, bonds),
    molecule,
    moleculeNodeIdToIndex
  );
}

/** ADR 0026: RDKit's layout with each hapto ligand moved next to its metal (placeHaptoLigands). */
function withHaptoLigandsPlaced(
  molecule: ChemMoleculeV1Document,
  position: Map<string, { readonly x: number; readonly y: number }>
): Map<string, { readonly x: number; readonly y: number }> {
  // RDKit lays bonds out 1.5 units long; a ligand's middle sits 1.6 bonds from its metal.
  if (molecule.haptoBonds) placeHaptoLigands(molecule.haptoBonds, molecule.graph.edges, position, 1.5 * 1.6);
  return position;
}

/** ADR 0026: the molecule's hapto bonds added to a depiction built from its atoms, by atom index. */
function withHaptoBonds(
  document: DrawingDocument,
  molecule: ChemMoleculeV1Document,
  indexOf: ReadonlyMap<string, number>
): DrawingDocument {
  const nodeIds = document.structure.graph.nodes.map((node) => node.id);
  const nodeOf = (id: string) => nodeIds[indexOf.get(id) ?? -1];
  return Object.values(molecule.haptoBonds ?? {}).reduce((drawing, bond) => {
    const metal = nodeOf(bond.metalAtomId);
    const ligand = bond.ligandAtomIds.map(nodeOf);
    return metal && ligand.every((id) => id !== undefined) ? addHaptoBond(drawing, metal, ligand as string[]) : drawing;
  }, document);
}

// Fallback path (the original #128 implementation): correct connectivity/atoms/charges/isotopes/
// radicals/aromaticity/coordinates and correct stereochemistry *data*, but no rendered wedge/hash
// -- depiction.bondStyles stays empty. Used when the wedge-accurate path (below) can't handle a
// particular Isomer (e.g. it contains a radical, which has no reliable standard SMILES encoding).
async function generateFlatIsomerDepiction(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  paperSize: PaperSize
): Promise<DrawingDocument> {
  const document = await generateMoleculeDepiction(molecule, paperSize, await openDoubleBonds(molecule, isomer));

  const moleculeNodeIdToIndex = new Map(molecule.graph.nodes.map((node, index) => [node.id, index]));
  const moleculeEdgeIdToIndex = moleculeEdgeIndexMaps(molecule);
  const newNodeIdByIndex = document.structure.graph.nodes.map((node) => node.id);
  const newEdgeIdByIndex = document.structure.graph.edges.map((edge) => edge.id);
  const stereochemistry = remapStereochemistry(
    isomer,
    moleculeNodeIdToIndex,
    moleculeEdgeIdToIndex,
    newNodeIdByIndex,
    newEdgeIdByIndex
  );

  return {
    ...document,
    structure: { ...document.structure, stereochemistry }
  };
}

// The wedge-accurate path (#130): builds a SMILES string that correctly encodes tetrahedral
// stereo (moleculeIsomerToSmiles -- see that module for the RDKit-validated reasoning), parses
// it, and lets RDKit's own get_new_coords() produce both fresh coordinates AND correct wedge
// codes in one pass (RDKit already perceives chirality correctly once fed a proper @/@@ tag, and
// its 2D-depiction generator transparently converts an explicit [H] atom back to an implicit-H
// wedge on a real bond -- confirmed empirically, see the plan/issue for this change). Double-bond
// cis/trans isn't SMILES-encoded (not needed -- renders directly from structure.stereochemistry.
// doubleBonds, no wedge involved), so it's remapped from the Isomer exactly as the flat path does.
async function generateWedgeAccurateIsomerDepiction(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  paperSize: PaperSize
): Promise<DrawingDocument> {
  const { smiles, atomOrder } = moleculeIsomerToSmiles(molecule, isomer);
  // Its SMILES carries no cis/trans, so RDKit's own "either" marks would cross every double bond
  // that can be E or Z; only those the Isomer leaves open are crossed (#1178).
  const crossed = await openDoubleBonds(molecule, isomer);
  const heavyAtomOrder = atomOrder.filter((id): id is string => id !== null);

  const rdkit = await loadRdKit();
  let rdkitMolecule: JSMol | null = null;
  let geometry;
  try {
    rdkitMolecule = rdkit.get_mol(smiles);
    if (!rdkitMolecule?.is_valid()) {
      throw new Error('RDKit could not sanitize the generated SMILES for this Molecule/Isomer.');
    }
    geometry = parseMolBlockGeometry(rdkitMolecule.get_new_coords(), heavyAtomOrder.length);
  } finally {
    rdkitMolecule?.delete();
  }

  const nodeIdToHeavyIndex = new Map(heavyAtomOrder.map((nodeId, index) => [nodeId, index]));
  const moleculeEdgeIdToIndex = moleculeEdgeIndexMaps(molecule);
  const { atomIds: aromaticAtomIds, bondIds: aromaticBondIds } = aromaticIdSets(molecule);

  const placed = withHaptoLigandsPlaced(
    molecule,
    new Map(heavyAtomOrder.map((nodeId, index) => [nodeId, geometry.coordinates[index]!]))
  );
  const atoms: ChemicalStructureAtom[] = heavyAtomOrder.map((nodeId) => {
    const atom = molecule.atoms[nodeId]!;
    return {
      atomicNumber: atom.atomicNumber,
      formalCharge: molecule.formalCharges[nodeId]!,
      x: placed.get(nodeId)!.x,
      y: placed.get(nodeId)!.y,
      aromatic: aromaticAtomIds.has(nodeId),
      ...(atom.isotope !== undefined ? { isotope: atom.isotope } : {}),
      ...(atom.radicalElectronCount !== undefined ? { radicalElectronCount: atom.radicalElectronCount } : {})
    };
  });

  const bonds: ChemicalStructureBond[] = molecule.graph.edges.map((edge) => {
    const sourceIndex = nodeIdToHeavyIndex.get(edge.source)!;
    const targetIndex = nodeIdToHeavyIndex.get(edge.target)!;
    const wedge = geometry.bondStereo.find(
      (candidate) =>
        (candidate.fromAtomIndex === sourceIndex && candidate.toAtomIndex === targetIndex) ||
        (candidate.fromAtomIndex === targetIndex && candidate.toAtomIndex === sourceIndex)
    );
    return {
      sourceAtomIndex: sourceIndex,
      targetAtomIndex: targetIndex,
      order: molecule.bonds[edge.id]!.order as BondOrder,
      aromatic: aromaticBondIds.has(edge.id),
      ...(crossed.has(edge.id) ? { crossed: true } : {}),
      ...(wedge && [1, 6].includes(wedge.code)
        ? {
            wedgeStyle: wedge.code === 1 ? ('solid' as const) : ('hashed' as const),
            wedgeFromAtomIndex: wedge.fromAtomIndex
          }
        : {})
    };
  });

  const document = createDrawingDocumentFromChemicalStructure(paperSize, atoms, bonds);
  const newNodeIdByIndex = document.structure.graph.nodes.map((node) => node.id);
  const newEdgeIdByIndex = document.structure.graph.edges.map((edge) => edge.id);
  const stereochemistry = remapStereochemistry(
    isomer,
    nodeIdToHeavyIndex,
    moleculeEdgeIdToIndex,
    newNodeIdByIndex,
    newEdgeIdByIndex
  );

  return withHaptoBonds(
    { ...document, structure: { ...document.structure, stereochemistry } },
    molecule,
    nodeIdToHeavyIndex
  );
}

// Given a persisted Molecule (graph/atoms/bonds/formalCharges) and one of its Isomers
// (stereochemistry), generates a fresh 2D layout -- including correctly rendered wedge/hash bonds
// -- and returns a full DrawingDocument suitable for drawingDocumentToSvg. Neither entity stores
// coordinates -- depiction is deliberately owned by whichever document references an Isomer, not
// the Isomer itself. Falls back to a flat (no-wedge, but otherwise fully correct) depiction if the
// wedge-accurate path can't handle this particular Isomer, rather than failing the whole render.
export async function generateIsomerDepiction(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  paperSize: PaperSize
): Promise<DrawingDocument> {
  let document: DrawingDocument;
  try {
    document = await generateWedgeAccurateIsomerDepiction(molecule, isomer, paperSize);
  } catch {
    document = await generateFlatIsomerDepiction(molecule, isomer, paperSize);
  }
  return withDoubleBondsAsGiven(document);
}

/**
 * #1184: a Molecule drawn as one of its Isomers, in the Molecule's own atom order (so a search
 * highlight, given by the Molecule's ids, still lines up): its double bonds as the Isomer gives
 * them -- E or Z, or crossed where it leaves them open. No wedges (as a molecule card has none).
 */
export async function generateMoleculeDepictionAs(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  paperSize: PaperSize
): Promise<DrawingDocument> {
  return withDoubleBondsAsGiven(await generateFlatIsomerDepiction(molecule, isomer, paperSize));
}

/**
 * #1184: each double bond's cis/trans an Isomer gives, keyed by bond, told the same way whichever
 * neighbours it was given against -- so two Isomers that say the same thing compare equal.
 */
export function doubleBondConfigurations(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document
): Record<string, 'cis' | 'trans'> {
  const neighbours = (atomId: string, except: string) =>
    molecule.graph.edges.flatMap((edge) =>
      edge.source === atomId && edge.target !== except
        ? [edge.target]
        : edge.target === atomId && edge.source !== except
          ? [edge.source]
          : []
    );
  const result: Record<string, 'cis' | 'trans'> = {};
  for (const [bondId, stereo] of Object.entries(isomer.stereochemistry.doubleBonds)) {
    const bond = molecule.graph.edges.find((edge) => edge.id === bondId);
    if (!bond) continue;
    let configuration = stereo.relativeConfiguration;
    for (const reference of stereo.referenceAtomIds) {
      const end = neighbours(reference, '').find((id) => id === bond.source || id === bond.target);
      if (!end) continue;
      const partner = end === bond.source ? bond.target : bond.source;
      const lowest = [...neighbours(end, partner)].sort()[0];
      if (lowest !== undefined && lowest !== reference) configuration = configuration === 'cis' ? 'trans' : 'cis';
    }
    result[bondId] = configuration;
  }
  return result;
}

/**
 * #1184: the layout knows nothing of cis and trans -- the layout SMILES carries none, and RDKit
 * drops them from its JSON -- so RDKit draws each double bond its own way, and a Z isomer came out
 * looking E. Where a drawn double bond shows the other configuration than the Isomer gives, the
 * atoms on one side are mirrored across it (a mirror keeps every other cis/trans on that side) and
 * their wedges swapped, so each stereocentre keeps its configuration (#1047). A double bond in a
 * ring is left as drawn: its sides don't come apart.
 */
function withDoubleBondsAsGiven(document: DrawingDocument): DrawingDocument {
  const { edges } = document.structure.graph;
  const neighbours = (atomId: string, except?: string) =>
    edges.flatMap((edge) => {
      if (edge.id === except) return [];
      return edge.source === atomId ? [edge.target] : edge.target === atomId ? [edge.source] : [];
    });
  let result = document;
  for (const [bondId, stereo] of Object.entries(document.structure.stereochemistry.doubleBonds)) {
    const bond = edges.find((edge) => edge.id === bondId);
    if (!bond) continue;
    const [first, second] = stereo.referenceAtomIds;
    const startAtom = neighbours(first).find((id) => id === bond.source || id === bond.target);
    const endAtom = startAtom === bond.source ? bond.target : bond.source;
    if (!startAtom || !neighbours(second).includes(endAtom)) continue;
    const at = (id: string) => result.depiction.coordinateSet.coordinates[id]!;
    const start = at(startAtom);
    const end = at(endAtom);
    const side = (id: string) => {
      const point = at(id);
      return Math.sign((end.x - start.x) * (point.y - start.y) - (end.y - start.y) * (point.x - start.x));
    };
    const drawnCis = side(first) === side(second);
    if (drawnCis === (stereo.relativeConfiguration === 'cis')) continue;
    // The atoms on the end side, reached without crossing the double bond.
    const moving = new Set([endAtom]);
    const queue = [endAtom];
    while (queue.length) {
      for (const next of neighbours(queue.pop()!, bondId)) {
        if (!moving.has(next)) {
          moving.add(next);
          queue.push(next);
        }
      }
    }
    if (moving.has(startAtom)) continue; // a ring
    result = swapWedgesFrom(mirrored(result, moving, start, end), moving);
  }
  return result === document ? document : keptInPlace(document, result);
}

/** `atomIds` mirrored across the line through `start` and `end`. */
function mirrored(
  document: DrawingDocument,
  atomIds: ReadonlySet<string>,
  start: { readonly x: number; readonly y: number },
  end: { readonly x: number; readonly y: number }
): DrawingDocument {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = dx * dx + dy * dy;
  const coordinates = Object.fromEntries(
    Object.entries(document.depiction.coordinateSet.coordinates).map(([id, point]) => {
      if (!atomIds.has(id) || length === 0) return [id, point];
      const along = ((point.x - start.x) * dx + (point.y - start.y) * dy) / length;
      const footX = start.x + along * dx;
      const footY = start.y + along * dy;
      return [id, { ...point, x: 2 * footX - point.x, y: 2 * footY - point.y }];
    })
  );
  return {
    ...document,
    depiction: { ...document.depiction, coordinateSet: { ...document.depiction.coordinateSet, coordinates } }
  };
}

/** `changed` moved back to where `original`'s structure started, its coordinate space grown to hold it. */
function keptInPlace(original: DrawingDocument, changed: DrawingDocument): DrawingDocument {
  const box = (document: DrawingDocument) => {
    const points = Object.values(document.depiction.coordinateSet.coordinates);
    return {
      minX: Math.min(...points.map((point) => point.x)),
      minY: Math.min(...points.map((point) => point.y)),
      maxX: Math.max(...points.map((point) => point.x)),
      maxY: Math.max(...points.map((point) => point.y))
    };
  };
  const before = box(original);
  const after = box(changed);
  const shiftX = before.minX - after.minX;
  const shiftY = before.minY - after.minY;
  const { coordinateSet } = changed.depiction;
  return {
    ...changed,
    depiction: {
      ...changed.depiction,
      coordinateSet: {
        ...coordinateSet,
        width: Math.max(coordinateSet.width, after.maxX + shiftX + before.minX),
        height: Math.max(coordinateSet.height, after.maxY + shiftY + before.minY),
        coordinates: Object.fromEntries(
          Object.entries(coordinateSet.coordinates).map(([id, point]) => [
            id,
            { ...point, x: point.x + shiftX, y: point.y + shiftY }
          ])
        )
      }
    }
  };
}
